// src/scenes/ReplayScene.js
// Replays a recorded game with sticker overlay + watermark + video export.
// Launched from LevelScene victory flow with replayData payload.

import Phaser from "phaser";
import { tileColorToHex, cssToPhaser, Theme } from "../config/theme.js";
import { RegionThemes, hexToPhaser } from "../data/regionThemes.js";
import { createBackground } from "../vfx/background.js";
import { ClipExporter } from "../game/ClipExporter.js";
import { Settings } from "../game/Settings.js";
import { I18n } from "../game/I18n.js";

// Reuse the gem drawing/texture from board.js
import { Board } from "../game/board.js";
import moodData from "../data/mood_data.json";

// Face portraits for comm panel
import faceFogo     from "../assets/monsters/faces/fogo_face.png";
import faceRaio     from "../assets/monsters/faces/raio_face.png";
import faceJato     from "../assets/monsters/faces/jato_face.png";
import faceTerra    from "../assets/monsters/faces/terra_face.png";
import faceControlo from "../assets/monsters/faces/controlo_face.png";

// Full-body hero art for stickers
import artFogo     from "../assets/monsters/fire/fire.png";
import artRaio     from "../assets/monsters/electric/electric.png";
import artJato     from "../assets/monsters/water/water.png";
import artTerra    from "../assets/monsters/earth/earth.png";
import artControlo from "../assets/monsters/psychic/psychic.png";

// Custom stickers
import stickerPrismara from "../assets/stickers/prismara.png";
import sticker1  from "../assets/stickers/1.png";
import sticker2  from "../assets/stickers/2.png";
import sticker3  from "../assets/stickers/3.png";
import sticker4  from "../assets/stickers/4.png";
import sticker5  from "../assets/stickers/5.png";
import sticker6  from "../assets/stickers/6.png";
import sticker8  from "../assets/stickers/8.png";
import sticker9  from "../assets/stickers/9.png";
import sticker10 from "../assets/stickers/10.png";
import sticker11 from "../assets/stickers/11.png";
import sticker12 from "../assets/stickers/12.png";
import sticker13 from "../assets/stickers/13.png";
import sticker14 from "../assets/stickers/14.png";
import sticker15 from "../assets/stickers/15.png";
import sticker16 from "../assets/stickers/16.png";
import sticker17 from "../assets/stickers/17.png";
import sticker18 from "../assets/stickers/18.png";
import sticker19 from "../assets/stickers/19.png";
import sticker20 from "../assets/stickers/20.png";
import sticker21 from "../assets/stickers/21.png";
import sticker22 from "../assets/stickers/22.png";
import sticker23 from "../assets/stickers/23.png";
import sticker24 from "../assets/stickers/24.png";
import sticker25 from "../assets/stickers/25.png";
import stickerNoxys1 from "../assets/stickers/noxys_sticker1.png";
import stickerNoxys2 from "../assets/stickers/noxys_sticker2.png";

const CUSTOM_STICKERS = {
  'stk-prismara': stickerPrismara,
  'stk-1': sticker1,   'stk-2': sticker2,   'stk-3': sticker3,
  'stk-4': sticker4,   'stk-5': sticker5,   'stk-6': sticker6,
  'stk-8': sticker8,   'stk-9': sticker9,   'stk-10': sticker10,
  'stk-11': sticker11, 'stk-12': sticker12, 'stk-13': sticker13,
  'stk-14': sticker14, 'stk-15': sticker15, 'stk-16': sticker16,
  'stk-17': sticker17, 'stk-18': sticker18, 'stk-19': sticker19,
  'stk-20': sticker20, 'stk-21': sticker21, 'stk-22': sticker22,
  'stk-23': sticker23, 'stk-24': sticker24, 'stk-25': sticker25,
  'stk-noxys1': stickerNoxys1, 'stk-noxys2': stickerNoxys2,
};

const GOLD = 0xfbbf24;
const PANEL_BG = 0x0d1525;

// Monster definitions — matches LevelScene order
const MONSTERS = [
  { id: "fogo",     name: "Pyron",   element: "fire",     speaker: "pyron",   color: "#ff4400" },
  { id: "raio",     name: "Voltrix", element: "electric", speaker: "voltrix", color: "#ffcc00" },
  { id: "jato",     name: "Aquyra",  element: "water",    speaker: "aquyra",  color: "#38bdf8" },
  { id: "terra",    name: "Sylrana", element: "earth",    speaker: "sylrana", color: "#4ade80" },
  { id: "controlo", name: "Athena",  element: "psychic",  speaker: "athena",  color: "#c084fc" },
];

// Color → monster index mapping (for combo reactions)
const COLOR_TO_MONSTER = { red: 0, yellow: 1, blue: 2, green: 3, purple: 4 };

export default class ReplayScene extends Phaser.Scene {
  constructor() {
    super("ReplayScene");
  }

  init(data) {
    this.replayData = data.replayData;
    this.returnTo = data.returnTo || "LootboxScene";
    this.returnData = data.returnData || {};
  }

  preload() {
    const faces = { fogo: faceFogo, raio: faceRaio, jato: faceJato, terra: faceTerra, controlo: faceControlo };
    for (const [id, url] of Object.entries(faces)) {
      if (!this.textures.exists(`face-${id}`)) {
        this.load.image(`face-${id}`, url);
      }
    }
    const arts = { fogo: artFogo, raio: artRaio, jato: artJato, terra: artTerra, controlo: artControlo };
    for (const [id, url] of Object.entries(arts)) {
      if (!this.textures.exists(`art-${id}`)) {
        this.load.image(`art-${id}`, url);
      }
    }
    for (const [key, url] of Object.entries(CUSTOM_STICKERS)) {
      if (!this.textures.exists(key)) {
        this.load.image(key, url);
      }
    }
  }

  create() {
    const W = this.scale.width;  // 540
    const H = this.scale.height; // 960
    const rd = this.replayData;

    this.W = W;
    this.H = H;

    // ── Background ──────────────────────────────────────────────────────
    const boardW = 8 * 58;
    const boardH = 8 * 58;
    const boardX = (W - boardW) / 2;
    const boardY = 120;
    this.boardX = boardX;
    this.boardY = boardY;
    this.tileSize = 58;

    const { boardFrame } = createBackground(this, rd.regionId, {
      boardX, boardY, boardW, boardH,
      analyserGetter: () => null,
    });

    // ── Build initial board ─────────────────────────────────────────────
    this.gems = [];  // 2D array of sprite references
    this._buildBoard(rd.initialBoard);

    // ── HUD-like overlays ───────────────────────────────────────────────
    // Score display
    this.scoreText = this.add.text(W / 2, 40, `SCORE: 0`, {
      fontFamily: "Kamikaze, system-ui, Arial",
      fontSize: "28px",
      color: "#fbbf24",
      stroke: "#0b0f14",
      strokeThickness: 4,
    }).setOrigin(0.5).setDepth(500);

    // Turn counter
    this.turnText = this.add.text(W / 2, 72, "", {
      fontFamily: "system-ui, Arial",
      fontSize: "14px",
      color: "#94a3b8",
    }).setOrigin(0.5).setDepth(500);

    // ── Combo text (reusable) ───────────────────────────────────────────
    this.comboText = this.add.text(W / 2, 250, "", {
      fontFamily: "Kamikaze, system-ui, Arial",
      fontSize: "36px",
      color: "#facc15",
      stroke: "#000000",
      strokeThickness: 5,
    }).setOrigin(0.5).setDepth(600).setAlpha(0);

    // ── Watermark (top-right, above board) ─────────────────────────────
    this.add.text(W - 12, 16, "PAZURUMON", {
      fontFamily: "Kamikaze, system-ui, Arial",
      fontSize: "14px",
      color: "#fbbf24",
      stroke: "#0b0f14",
      strokeThickness: 3,
    }).setOrigin(1, 0).setDepth(900).setAlpha(0.5);

    // ── Comm panel objects (Star Fox style) ────────────────────────────
    this._commObjs = [];

    // ── Sticker layer container ─────────────────────────────────────────
    this.stickerLayer = this.add.container(0, 0).setDepth(800);
    this.stickers = [];

    // ── Control bar (bottom) ────────────────────────────────────────────
    this._buildControlBar();

    // ── State ───────────────────────────────────────────────────────────
    this._playing = false;
    this._turnIdx = 0;
    this._exporter = null;
    this._speed = 1; // 1x, 1.5x, 2x

    // ── Start replay automatically ──────────────────────────────────────
    this.time.delayedCall(600, () => this._startReplay());
  }

  // ── Build the tile grid from a color snapshot ─────────────────────────
  _buildBoard(snapshot) {
    // Clear existing
    if (this.gems) {
      for (const row of this.gems) {
        for (const gem of row) {
          if (gem) gem.destroy();
        }
      }
    }
    this.gems = [];

    for (let r = 0; r < snapshot.length; r++) {
      const row = [];
      for (let c = 0; c < snapshot[r].length; c++) {
        const color = snapshot[r][c];
        if (color) {
          const gem = this._createGem(r, c, color);
          row.push(gem);
        } else {
          row.push(null);
        }
      }
      this.gems.push(row);
    }
  }

  _createGem(r, c, color) {
    const x = this.boardX + c * this.tileSize + this.tileSize / 2;
    const y = this.boardY + r * this.tileSize + this.tileSize / 2;
    const S = this.tileSize - 8;
    const colorHex = tileColorToHex(color);

    // Use the cached gem textures from board.js
    const style = Settings.tileStyle;
    const texKey = `gem_${style}_${colorHex.toString(16)}_${S}`;

    let gem;
    if (this.textures.exists(texKey)) {
      gem = this.add.image(x, y, texKey).setDepth(100);
    } else {
      // Fallback: simple colored rectangle
      gem = this.add.rectangle(x, y, S, S, colorHex, 1).setDepth(100);
    }
    gem._tileColor = color;
    return gem;
  }

  _worldPos(r, c) {
    return {
      x: this.boardX + c * this.tileSize + this.tileSize / 2,
      y: this.boardY + r * this.tileSize + this.tileSize / 2,
    };
  }

  // ── Main replay loop ──────────────────────────────────────────────────
  async _startReplay() {
    this._playing = true;
    this._turnIdx = 0;
    const rd = this.replayData;

    this.scoreText.setText("SCORE: 0");

    for (let i = 0; i < rd.turns.length; i++) {
      if (!this._playing) break;
      this._turnIdx = i;
      this.turnText.setText(`${I18n.t('replay_turn') || 'TURN'} ${i + 1}/${rd.turns.length}`);

      await this._playTurn(rd.turns[i]);

      // Brief pause between turns
      await this._wait(200);
    }

    if (this._playing && rd.result?.won) {
      await this._playVictory(rd.result);
    }

    this._playing = false;
    this._onReplayFinished();
  }

  async _playTurn(turn) {
    const { swap, cascades, scoreAfter } = turn;
    const spd = this._speed;

    // ── 1. Animate swap ─────────────────────────────────────────────────
    const gemA = this.gems[swap.from.r]?.[swap.from.c];
    const gemB = this.gems[swap.to.r]?.[swap.to.c];

    if (gemA && gemB) {
      const posA = this._worldPos(swap.from.r, swap.from.c);
      const posB = this._worldPos(swap.to.r, swap.to.c);

      // Swap in gems array
      this.gems[swap.from.r][swap.from.c] = gemB;
      this.gems[swap.to.r][swap.to.c] = gemA;

      await Promise.all([
        this._tween(gemA, { x: posB.x, y: posB.y, duration: 105 / spd, ease: "Back.Out(1.4)" }),
        this._tween(gemB, { x: posA.x, y: posA.y, duration: 105 / spd, ease: "Back.Out(1.4)" }),
      ]);
    }

    // ── 2. Play each cascade ────────────────────────────────────────────
    for (const cascade of cascades) {
      if (!this._playing) return;
      await this._playCascade(cascade);
    }

    // Update score
    this.scoreText.setText(`SCORE: ${scoreAfter}`);
  }

  async _playCascade(cascade) {
    const { matched, comboMultiplier, scoreGained, boardAfter } = cascade;
    const spd = this._speed;

    // ── Flash matched tiles ─────────────────────────────────────────────
    const matchedGems = [];
    for (const pos of matched) {
      const gem = this.gems[pos.r]?.[pos.c];
      if (gem) matchedGems.push(gem);
    }

    // White flash effect
    for (const gem of matchedGems) {
      if (gem.setTint) gem.setTint(0xffffff);
    }
    await this._wait(80 / spd);

    // ── Show combo text + creature reaction ──────────────────────────────
    if (comboMultiplier >= 2) {
      this._showCombo(comboMultiplier);
      this._showReaction(comboMultiplier, matched);
    }

    // ── Remove matched tiles (scale + fade out) ─────────────────────────
    const removePromises = matchedGems.map(gem =>
      this._tween(gem, { scaleX: 0.1, scaleY: 0.1, alpha: 0, duration: 120 / spd, ease: "Sine.In" })
    );
    await Promise.all(removePromises);

    // Destroy matched gems
    for (const pos of matched) {
      const gem = this.gems[pos.r]?.[pos.c];
      if (gem) {
        gem.destroy();
        this.gems[pos.r][pos.c] = null;
      }
    }

    // ── Rebuild board from snapshot (gravity + new tiles) ────────────────
    await this._transitionBoard(boardAfter);

    await this._wait(60 / spd);
  }

  // Smoothly transition to a new board state
  async _transitionBoard(boardAfter) {
    const spd = this._speed;
    const tweens = [];

    // First, figure out what tiles exist and where they need to go
    // Destroy any remaining gems that don't match the new board
    for (let r = 0; r < boardAfter.length; r++) {
      for (let c = 0; c < boardAfter[r].length; c++) {
        const existing = this.gems[r]?.[c];
        const newColor = boardAfter[r][c];

        if (existing && existing._tileColor !== newColor) {
          existing.destroy();
          this.gems[r][c] = null;
        }
      }
    }

    // Find tiles that need to fall (exist somewhere above their target)
    // Simple approach: rebuild all missing positions with fall-in animation
    for (let r = 0; r < boardAfter.length; r++) {
      for (let c = 0; c < boardAfter[r].length; c++) {
        const target = this._worldPos(r, c);

        if (!this.gems[r]?.[c] && boardAfter[r][c]) {
          // Need a new gem here — spawn above and fall in
          const gem = this._createGem(r, c, boardAfter[r][c]);
          gem.y = this.boardY - this.tileSize; // start above board
          gem.alpha = 0.8;
          this.gems[r][c] = gem;
          tweens.push(this._tween(gem, {
            y: target.y, alpha: 1,
            duration: 160 / spd, ease: "Sine.InOut",
          }));
        } else if (this.gems[r]?.[c]) {
          // Existing gem — make sure it's at the right position (gravity)
          const gem = this.gems[r][c];
          if (Math.abs(gem.y - target.y) > 1 || Math.abs(gem.x - target.x) > 1) {
            tweens.push(this._tween(gem, {
              x: target.x, y: target.y,
              duration: 140 / spd, ease: "Sine.InOut",
            }));
          }
        }
      }
    }

    if (tweens.length > 0) {
      await Promise.all(tweens);
    }
  }

  _showCombo(multiplier) {
    const labels = ['', '', 'DOUBLE!', 'TRIPLE!', 'QUAD!', 'PENTA!'];
    const label = multiplier < labels.length
      ? labels[multiplier]
      : `×${multiplier} COMBO!`;

    this.comboText.setText(label);
    this.comboText.setAlpha(1).setScale(0.3);
    this.tweens.add({
      targets: this.comboText,
      scaleX: 1.2, scaleY: 1.2,
      duration: 200, ease: "Back.Out(2)",
      onComplete: () => {
        this.tweens.add({
          targets: this.comboText,
          scaleX: 1, scaleY: 1, alpha: 0,
          duration: 800, delay: 400, ease: "Quad.Out",
        });
      },
    });
  }

  // ── Star Fox comm panel — slides in from bottom on combos ──────────
  _showReaction(comboMultiplier, matchedPositions) {
    // Pick a random monster
    const monsterIdx = Math.floor(Math.random() * MONSTERS.length);
    const monster = MONSTERS[monsterIdx];

    // Pick a quip from mood_data
    const locale = I18n.locale || 'en-US';
    const triggerKey = comboMultiplier >= 3 ? 'combo_big' : 'combo_small';
    const allQuips = moodData.individual?.filter(q =>
      q.triggers?.includes(triggerKey)
    ) || [];

    let quipText = comboMultiplier >= 3 ? '🔥🔥🔥' : '✨';
    if (allQuips.length > 0) {
      const pick = allQuips[Math.floor(Math.random() * allQuips.length)];
      quipText = pick.text?.[locale] || pick.text?.['en-US'] || pick.text?.['pt-PT'] || quipText;
    }

    this._showCommPanel(monster, quipText);
  }

  _showCommPanel(monster, text, duration = 3500) {
    // Destroy previous comm
    this._destroyComm();

    const W = this.W;
    const panelH = 120;
    const panelY = this.H - 90 - panelH; // above control bar
    const portraitSize = 90;
    const borderColor = Phaser.Display.Color.HexStringToColor(monster.color).color;
    const objs = [];

    // Panel background — dark translucent bar, full width
    const panelBg = this.add.rectangle(W / 2, panelY + panelH / 2, W - 20, panelH, 0x0a0e1a, 0.94)
      .setStrokeStyle(2, borderColor, 0.8)
      .setDepth(700);
    objs.push(panelBg);

    // Portrait frame (left side)
    const pX = 24 + portraitSize / 2;
    const pY = panelY + panelH / 2;

    // Portrait border (colored ring)
    const portraitBorder = this.add.graphics().setDepth(701);
    portraitBorder.fillStyle(borderColor, 1);
    portraitBorder.fillCircle(pX, pY, portraitSize / 2 + 3);
    portraitBorder.fillStyle(0x0a0e1a, 1);
    portraitBorder.fillCircle(pX, pY, portraitSize / 2);
    objs.push(portraitBorder);

    // Portrait sprite — use face close-up, fall back to full-body
    const faceKey = `face-${monster.id}`;
    const fallbackKey = `monster-${monster.id}`;
    const texKey = this.textures.exists(faceKey) ? faceKey : fallbackKey;
    if (this.textures.exists(texKey)) {
      const portrait = this.add.image(pX, pY, texKey)
        .setDisplaySize(portraitSize - 6, portraitSize - 6)
        .setDepth(702);

      // Circular mask
      const maskGfx = this.add.graphics().setVisible(false);
      maskGfx.fillCircle(pX, pY, (portraitSize - 6) / 2);
      portrait.setMask(maskGfx.createGeometryMask());
      objs.push(portrait, maskGfx);
    }

    // Name label
    const textLeft = 24 + portraitSize + 16;
    const nameText = this.add.text(textLeft, panelY + 18, monster.name.toUpperCase(), {
      fontFamily: 'Kamikaze, system-ui, Arial',
      fontSize: '16px',
      color: monster.color,
    }).setOrigin(0, 0).setDepth(703);
    objs.push(nameText);

    // Speech text
    const speechText = this.add.text(textLeft, panelY + 44, text, {
      fontFamily: 'system-ui, Arial',
      fontSize: '18px',
      color: '#e2e8f0',
      wordWrap: { width: W - portraitSize - 80 },
      lineSpacing: 4,
    }).setOrigin(0, 0).setDepth(703);
    objs.push(speechText);

    this._commObjs = objs;

    // Slide in from below
    for (const o of objs) {
      if (o.y !== undefined) o.y += panelH + 10;
    }
    this.tweens.add({
      targets: objs,
      y: `-=${panelH + 10}`,
      duration: 450,
      ease: 'Back.Out(1.4)',
    });

    // Auto-dismiss
    this.time.delayedCall(duration, () => this._dismissComm());
  }

  _dismissComm() {
    if (this._commObjs.length === 0) return;
    this.tweens.add({
      targets: this._commObjs,
      y: `+=${130}`,
      alpha: 0,
      duration: 400,
      ease: 'Sine.In',
      onComplete: () => this._destroyComm(),
    });
  }

  _destroyComm() {
    for (const o of this._commObjs) {
      try {
        if (o?.mask) o.mask.destroy();
        o?.destroy();
      } catch (_) {}
    }
    this._commObjs = [];
  }

  async _playVictory(result) {
    const theme = RegionThemes[this.replayData.regionId] || RegionThemes.volcano;
    const primary = hexToPhaser(theme.primary);

    // Victory comm panel — random creature celebrates
    const locale = I18n.locale || 'en-US';
    const victoryQuips = moodData.individual?.filter(q =>
      q.triggers?.includes('victory_clean') || q.triggers?.includes('victory_clutch')
    ) || [];
    const victoryMonster = MONSTERS[Math.floor(Math.random() * MONSTERS.length)];
    let victoryText = '🎉🎉🎉';
    if (victoryQuips.length > 0) {
      const pick = victoryQuips[Math.floor(Math.random() * victoryQuips.length)];
      victoryText = pick.text?.[locale] || pick.text?.['en-US'] || pick.text?.['pt-PT'] || victoryText;
    }
    this._showCommPanel(victoryMonster, victoryText, 3500);

    // Big score reveal
    this.scoreText.setText(`SCORE: ${result.score.toLocaleString()}`);
    this.tweens.add({
      targets: this.scoreText,
      scaleX: 1.3, scaleY: 1.3,
      duration: 300, ease: "Back.Out(2)",
      yoyo: true,
    });

    // ── Infinite mode: show wave + max combo instead of stars ────────────
    if (result.infinite) {
      const infoY = this.H * 0.45;
      await this._wait(300);

      const waveText = this.add.text(this.W / 2, infoY, `WAVE ${result.wave}`, {
        fontSize: '48px', fontFamily: 'Kamikaze, Arial',
        color: '#d084fc',
        stroke: '#1a0a2e',
        strokeThickness: 5,
      }).setOrigin(0.5).setScale(0).setDepth(700);

      this.tweens.add({
        targets: waveText, scaleX: 1, scaleY: 1,
        duration: 300, ease: 'Back.Out(2.5)',
      });

      if (result.maxCombo >= 2) {
        await this._wait(400);
        const comboInfo = this.add.text(this.W / 2, infoY + 55, `MAX COMBO ×${result.maxCombo}`, {
          fontSize: '22px', fontFamily: 'Kamikaze, Arial',
          color: '#fbbf24',
          stroke: '#0b0f14',
          strokeThickness: 3,
        }).setOrigin(0.5).setScale(0).setDepth(700);

        this.tweens.add({
          targets: comboInfo, scaleX: 1, scaleY: 1,
          duration: 250, ease: 'Back.Out(2)',
        });
      }
    } else {
      // ── Normal mode: stars ────────────────────────────────────────────
      const starY = this.H * 0.45;
      const gap = 80;
      const sx0 = this.W / 2 - gap;
      for (let i = 0; i < 3; i++) {
        const filled = i < result.stars;
        await this._wait(200);
        const star = this.add.text(sx0 + i * gap, starY, filled ? '★' : '☆', {
          fontSize: '64px', fontFamily: 'Kamikaze, Arial',
          color: filled ? '#facc15' : '#4b5563',
          stroke: filled ? '#92400e' : '#111827',
          strokeThickness: 5,
        }).setOrigin(0.5).setScale(0).setDepth(700);

      this.tweens.add({
        targets: star, scaleX: 1, scaleY: 1,
        duration: 270, ease: 'Back.Out(2.5)',
      });
      }
    }

    // Confetti burst
    const confetti = [primary, 0xffffff, 0xfacc15];
    for (let i = 0; i < 60; i++) {
      this.time.delayedCall(i * 15, () => {
        const x = Phaser.Math.Between(10, this.W - 10);
        const sz = Phaser.Math.FloatBetween(4, 10);
        const c = confetti[Math.floor(Math.random() * confetti.length)];
        const p = this.add.rectangle(x, -8, sz, sz, c, 1).setDepth(650);
        this.tweens.add({
          targets: p,
          y: this.H + 20,
          x: x + Phaser.Math.Between(-70, 70),
          alpha: 0,
          duration: Phaser.Math.Between(800, 1600),
          ease: 'Quad.In',
          onComplete: () => p.destroy(),
        });
      });
    }

    await this._wait(2500);
  }

  // ── Control bar ───────────────────────────────────────────────────────
  _buildControlBar() {
    const W = this.W;
    const H = this.H;
    const barY = H - 80;
    const barH = 80;

    // Bar background
    this.add.rectangle(W / 2, barY + barH / 2, W, barH, PANEL_BG, 0.95).setDepth(850);
    this.add.rectangle(W / 2, barY, W, 1, GOLD, 0.3).setDepth(851);

    // ── Buttons ─────────────────────────────────────────────────────────
    const btnStyle = {
      fontFamily: "system-ui, Arial",
      fontSize: "13px",
      color: "#cbd5e1",
      backgroundColor: "#1e293b",
      padding: { x: 14, y: 8 },
    };

    // Speed button
    this._speedBtn = this.add.text(70, barY + barH / 2, "1×", {
      ...btnStyle, fontSize: "15px", color: "#fbbf24",
    }).setOrigin(0.5).setDepth(860)
      .setInteractive({ useHandCursor: true })
      .on("pointerup", () => this._cycleSpeed());

    // Sticker button
    this.add.text(170, barY + barH / 2, I18n.t('replay_stickers') || "STICKERS", btnStyle)
      .setOrigin(0.5).setDepth(860)
      .setInteractive({ useHandCursor: true })
      .on("pointerup", () => this._openStickerPicker());

    // Screenshot button
    this.add.text(270, barY + barH / 2, I18n.t('replay_screenshot') || "📸", {
        ...btnStyle, fontSize: "15px",
      })
      .setOrigin(0.5).setDepth(860)
      .setInteractive({ useHandCursor: true })
      .on("pointerup", () => this._screenshot());

    // Record + Share button
    this._recordBtn = this.add.text(400, barY + barH / 2,
      I18n.t('replay_record') || "REC & SHARE", {
        ...btnStyle, color: "#0d1525", backgroundColor: "#fbbf24",
        fontStyle: "bold",
      })
      .setOrigin(0.5).setDepth(860)
      .setInteractive({ useHandCursor: true })
      .on("pointerup", () => this._recordAndShare());

    // Close button
    this.add.text(W - 40, barY + barH / 2, "✕", {
      fontFamily: "system-ui, Arial",
      fontSize: "22px",
      color: "#64748b",
    }).setOrigin(0.5).setDepth(860)
      .setInteractive({ useHandCursor: true })
      .on("pointerover", function () { this.setColor("#e2e8f0"); })
      .on("pointerout", function () { this.setColor("#64748b"); })
      .on("pointerup", () => this._exit());
  }

  _cycleSpeed() {
    const speeds = [1, 1.5, 2];
    const idx = speeds.indexOf(this._speed);
    this._speed = speeds[(idx + 1) % speeds.length];
    this._speedBtn.setText(`${this._speed}×`);
  }

  // ── Sticker Picker ────────────────────────────────────────────────────
  _openStickerPicker() {
    if (this._stickerPanelOpen) return;
    this._stickerPanelOpen = true;

    const W = this.W;
    const panelH = 440;
    const panelY = this.H - 80 - panelH; // above control bar
    const cols = 10;
    const cellW = 48;
    const cellH = 48;
    const padX = (W - cols * cellW) / 2;

    // Panel background + border
    const bg = this.add.rectangle(W / 2, panelY + panelH / 2, W, panelH, PANEL_BG, 0.95).setDepth(870);
    const border = this.add.rectangle(W / 2, panelY, W, 1, GOLD, 0.3).setDepth(871);

    const items = [];

    // ── Emoji stickers ──────────────────────────────────────────────────
    const emojiSet = [
      // Elemental
      '🔥', '💧', '⚡', '🌿', '🔮',
      // Game
      '⭐', '💥', '🎮', '👾', '🏆',
      // Reactions
      '😱', '🤯', '😎', '🥳', '💀',
      '😤', '🫡', '👑', '❤️', '💯',
      // Game-themed
      '🎯', '🧩', '💎', '🪄', '⚔️',
      '🛡️', '🏅', '🎪', '✨', '🚀',
      // World
      '🌋', '🌊', '🌳', '⛈️', '🧠',
      // Hype
      '💣', '🫶', '👀', '🎉', '🔮',
    ];

    emojiSet.forEach((emoji, i) => {
      const col = i % cols;
      const row = Math.floor(i / cols);
      const x = padX + col * cellW + cellW / 2;
      const y = panelY + 28 + row * cellH;
      const s = this.add.text(x, y, emoji, { fontSize: "28px" })
        .setOrigin(0.5).setDepth(880)
        .setInteractive({ useHandCursor: true })
        .on("pointerup", () => {
          this._addSticker({ type: 'emoji', value: emoji });
          this._closeStickerPicker(bg, border, items);
        });
      items.push(s);
    });

    // ── Image stickers (faces + full art) — row below emojis ─────────
    const emojiRows = Math.ceil(emojiSet.length / cols);
    const imgY = panelY + 28 + emojiRows * cellH + 12;

    // Label
    const label = this.add.text(padX, imgY - 6, 'PAZURUMON', {
      fontFamily: 'Kamikaze, system-ui, Arial', fontSize: '11px', color: '#fbbf24',
    }).setOrigin(0, 0.5).setDepth(880);
    items.push(label);

    const imageKeys = [
      'face-fogo', 'face-raio', 'face-jato', 'face-terra', 'face-controlo',
      'art-fogo', 'art-raio', 'art-jato', 'art-terra', 'art-controlo',
    ];

    imageKeys.forEach((key, i) => {
      if (!this.textures.exists(key)) return;
      const col = i % cols;
      const row = Math.floor(i / cols);
      const x = padX + col * cellW + cellW / 2;
      const y = imgY + 18 + row * cellH;
      const img = this.add.image(x, y, key)
        .setDisplaySize(38, 38).setDepth(880)
        .setInteractive({ useHandCursor: true })
        .on("pointerup", () => {
          this._addSticker({ type: 'image', value: key });
          this._closeStickerPicker(bg, border, items);
        });
      items.push(img);
    });

    // ── Custom stickers ─────────────────────────────────────────────────
    const creatureRows = Math.ceil(imageKeys.length / cols);
    const customY = imgY + 18 + creatureRows * cellH + 16;

    const customLabel = this.add.text(padX, customY - 6, 'STICKERS', {
      fontFamily: 'Kamikaze, system-ui, Arial', fontSize: '11px', color: '#fbbf24',
    }).setOrigin(0, 0.5).setDepth(880);
    items.push(customLabel);

    const customKeys = Object.keys(CUSTOM_STICKERS);
    customKeys.forEach((key, i) => {
      if (!this.textures.exists(key)) return;
      const col = i % cols;
      const row = Math.floor(i / cols);
      const x = padX + col * cellW + cellW / 2;
      const y = customY + 18 + row * cellH;
      const img = this.add.image(x, y, key)
        .setDisplaySize(38, 38).setDepth(880)
        .setInteractive({ useHandCursor: true })
        .on("pointerup", () => {
          this._addSticker({ type: 'image', value: key });
          this._closeStickerPicker(bg, border, items);
        });
      items.push(img);
    });

    // Close on tap outside
    this.time.delayedCall(200, () => {
      const outsideHit = this.add.rectangle(W / 2, panelY / 2, W, panelY, 0, 0)
        .setDepth(869).setInteractive();
      outsideHit.on("pointerup", () => {
        outsideHit.destroy();
        this._closeStickerPicker(bg, border, items);
      });
      items.push(outsideHit);
    });
  }

  _closeStickerPicker(bg, border, items) {
    bg.destroy();
    border.destroy();
    for (const s of items) if (s?.active) s.destroy();
    this._stickerPanelOpen = false;
  }

  _addSticker(data) {
    const x = this.W / 2;
    const y = this.H / 2;
    let sticker;

    if (data.type === 'image') {
      sticker = this.add.image(x, y, data.value)
        .setDisplaySize(90, 90).setDepth(810)
        .setInteractive({ draggable: true, useHandCursor: true });
    } else {
      sticker = this.add.text(x, y, data.value, {
        fontSize: "62px",
      }).setOrigin(0.5).setDepth(810)
        .setInteractive({ draggable: true, useHandCursor: true });
    }

    this.input.setDraggable(sticker);
    sticker.on("drag", (_ptr, dragX, dragY) => {
      sticker.x = dragX;
      sticker.y = dragY;
    });

    // Double-tap to remove
    let lastTap = 0;
    sticker.on("pointerup", () => {
      const now = Date.now();
      if (now - lastTap < 300) {
        sticker.destroy();
        this.stickers = this.stickers.filter(s => s !== sticker);
      }
      lastTap = now;
    });

    // Pop-in animation
    sticker.setScale(0);
    this.tweens.add({
      targets: sticker, scaleX: 1, scaleY: 1,
      duration: 200, ease: "Back.Out(2.5)",
    });

    this.stickers.push(sticker);
  }

  // ── Screenshot & Share ───────────────────────────────────────────────
  async _screenshot() {
    // Render one clean frame then grab the canvas
    this.game.renderer.snapshot(async (image) => {
      const canvas = document.createElement('canvas');
      canvas.width = image.width;
      canvas.height = image.height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(image, 0, 0);

      const blob = await new Promise(r => canvas.toBlob(r, 'image/png'));
      if (!blob) {
        this._showToast(I18n.t('replay_error') || 'Screenshot failed');
        return;
      }

      const file = new File([blob], 'pazurumon.png', { type: 'image/png' });
      const rd = this.replayData;
      const shareText = rd.result?.infinite
        ? `I hit Wave ${rd.result.wave} with ${rd.result.score.toLocaleString()} points in PAZURUMON Infinite Mode! 🎮🔥`
        : `I scored ${rd.result?.score?.toLocaleString() || 0} points! 🎮⭐`;

      // Try Web Share API
      if (navigator.canShare?.({ files: [file] })) {
        try {
          await navigator.share({ title: 'PAZURUMON', text: shareText, files: [file] });
          return;
        } catch (_) { /* user cancelled or failed — fallback to download */ }
      }

      // Fallback: download
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'pazurumon.png';
      a.click();
      URL.revokeObjectURL(url);
      this._showToast(I18n.t('replay_screenshot_saved') || 'Screenshot saved!');
    });
  }

  // ── Record and Share ──────────────────────────────────────────────────
  async _recordAndShare() {
    if (this._isRecording) return;

    if (!ClipExporter.isSupported()) {
      this._showToast(I18n.t('replay_not_supported') || "Recording not supported on this device");
      return;
    }

    this._isRecording = true;
    this._recordBtn.setText(I18n.t('replay_recording') || "RECORDING...");
    this._recordBtn.setColor("#ef4444");

    // Get the Phaser canvas
    const canvas = this.game.canvas;
    this._exporter = new ClipExporter(canvas);
    this._exporter.start(30);

    // Replay the game while recording
    this._playing = false; // stop current if running
    await this._wait(300);

    // Rebuild board from initial state
    this._buildBoard(this.replayData.initialBoard);
    await this._startReplay();

    // Stop recording
    const blob = await this._exporter.stop();

    this._isRecording = false;
    this._recordBtn.setText(I18n.t('replay_record') || "REC & SHARE");
    this._recordBtn.setColor("#0d1525");

    if (!blob) {
      this._showToast(I18n.t('replay_error') || "Recording failed");
      return;
    }

    // Try share, fallback to download
    const shared = await this._exporter.share(
      "PAZURUMON",
      this.replayData.result.infinite
        ? `I hit Wave ${this.replayData.result.wave} with ${this.replayData.result.score.toLocaleString()} points in PAZURUMON Infinite Mode! 🎮🔥`
        : `I scored ${this.replayData.result.score.toLocaleString()} points! 🎮⭐`
    );

    if (!shared) {
      this._showToast(I18n.t('replay_saved') || "Clip saved!");
    }
  }

  _showToast(text) {
    const toast = this.add.text(this.W / 2, this.H / 2, text, {
      fontFamily: "system-ui, Arial",
      fontSize: "16px",
      color: "#0d1525",
      backgroundColor: "#fbbf24",
      padding: { x: 20, y: 10 },
    }).setOrigin(0.5).setDepth(950).setAlpha(0);

    this.tweens.add({
      targets: toast, alpha: 1,
      duration: 200,
      onComplete: () => {
        this.tweens.add({
          targets: toast, alpha: 0,
          duration: 300, delay: 2000,
          onComplete: () => toast.destroy(),
        });
      },
    });
  }

  // ── Called when replay finishes ────────────────────────────────────────
  _onReplayFinished() {
    this.turnText.setText(I18n.t('replay_done') || "REPLAY COMPLETE");
  }

  // ── Exit back to game flow ────────────────────────────────────────────
  _exit() {
    this._playing = false;
    if (this._exporter) {
      this._exporter.destroy();
      this._exporter = null;
    }
    this.cameras.main.fadeOut(175, 0, 0, 0);
    this.cameras.main.once("camerafadeoutcomplete", () => {
      this.scene.start(this.returnTo, this.returnData);
    });
  }

  // ── Utility ───────────────────────────────────────────────────────────
  _tween(target, props) {
    return new Promise((resolve) => {
      if (!target?.active) return resolve();
      this.tweens.add({
        targets: target,
        ...props,
        onComplete: () => resolve(),
      });
    });
  }

  _wait(ms) {
    return new Promise((resolve) => {
      this.time.delayedCall(Math.max(1, ms), resolve);
    });
  }
}
